import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Users, Shield, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import "@/styles/fj.css";
import { roleColor } from "@/utils/fjMeta";
import linkifyText from '../utils/linkifyText';

const AdvancedJobDetailsModalForCrew = ({ job, isOpen, onClose, isDark = false }) => {
  const [applicationsStatus, setApplicationsStatus] = useState({}); // Track applied roles
  const [loading, setLoading] = useState({});
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedRoleToApply, setSelectedRoleToApply] = useState(null);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  useEffect(() => {
    if (isOpen && job) {
      loadApplicationStatus();
    }
  }, [isOpen, job]);

  const loadApplicationStatus = async () => {
    try {
      // Check which roles user has already applied for
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/my-applications`,
        { params: { user_email: user.email } }
      );

      if (response.data.applications) {
        const statusByRole = {};
        response.data.applications.forEach(app => {
          if (app.role) {
            statusByRole[app.role] = app.status; // 'pending', 'accepted', 'declined'
          }
        });
        setApplicationsStatus(statusByRole);
      }
    } catch (error) {
      console.log('No applications or error loading:', error);
    }
  };

  // Opens the apply modal for a specific role
  const handleOpenApplyModal = (role, crewMember) => {
    setSelectedRoleToApply({ role, crewMember });
    setApplicationMessage('');
    setShowApplyModal(true);
  };

  // Submits the application with the introduction message
  const handleSubmitApplication = async () => {
    if (!selectedRoleToApply) return;

    const { role, crewMember } = selectedRoleToApply;
    setSubmittingApplication(true);

    try {
      const applicantName = user.full_name || user.fullName || `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() || 'Unknown';

      // Use the main jobs/apply endpoint which sends emails
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/apply`,
        {
          job_id: job.id,
          job_title: job.title || job.job_title,
          applicant_name: applicantName,
          applicant_email: user.email,
          application_message: applicationMessage,
          job_owner_name: job.owner?.name || job.client_name || 'Job Owner',
          job_owner_email: job.owner?.email || job.client_email || job.owner_email,
          job_date: job.date || job.dates?.[0] || 'TBD',
          role: role
        }
      );

      // Update local status
      setApplicationsStatus(prev => ({ ...prev, [role]: 'pending' }));
      setShowApplyModal(false);
      setSelectedRoleToApply(null);
      setApplicationMessage('');
      toast.success(`Applied for ${role.replace(/_/g, ' ')} position!`);

    } catch (error) {
      console.error('Error applying for role:', error);
      toast.error('Failed to apply for position');
    } finally {
      setSubmittingApplication(false);
    }
  };

  const handleRemoveApplication = async (role) => {
    setLoading(prev => ({ ...prev, [role]: true }));

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/application`,
        {
          data: { user_email: user.email, role: role }
        }
      );

      // Update local status
      setApplicationsStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[role];
        return newStatus;
      });
      toast.success(`Removed application for ${role.replace(/_/g, ' ')}`);

    } catch (error) {
      console.error('Error removing application:', error);
      toast.error('Failed to remove application');
    } finally {
      setLoading(prev => ({ ...prev, [role]: false }));
    }
  };

  if (!isOpen || !job) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getCrewPayout = (amount) => {
    return Math.floor(amount * 0.98);
  };

  const getStatusConfig = (status) => {
    const configs = {
      PENDING_APPROVAL: {
        color: 'yellow',
        gradient: 'from-yellow-500 to-amber-500',
        label: 'Pending Approval',
        bgLight: 'bg-yellow-50'
      },
      SEEKING_APPLICANTS: {
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        label: 'Seeking Applicants',
        bgLight: 'bg-green-50'
      },
      PARTIALLY_HIRED: {
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        label: 'Seeking Applicants',
        bgLight: 'bg-green-50'
      }
    };
    return configs[status] || configs.SEEKING_APPLICANTS;
  };

  const statusConfig = getStatusConfig(job.status);
  const allCrewMembers = job.crewMembers || job.crew_members || [];
  const crewMembers = allCrewMembers.filter(member => !member.hired);

  // Get all unique dates (from all crew members including hired)
  const getAllJobDates = () => {
    if (allCrewMembers.length > 0 && allCrewMembers.some(m => m.dateAssignments)) {
      const allDates = new Set();
      allCrewMembers.forEach(member => {
        if (member.dateAssignments) {
          member.dateAssignments.forEach(assignment => {
            if (assignment.date) {
              allDates.add(assignment.date);
            }
          });
        }
      });
      return Array.from(allDates).sort();
    }
    return job.dates || [job.date];
  };

  const jobDates = getAllJobDates();
  const totalOpenEarnings = getCrewPayout(crewMembers.reduce((sum, m) => sum + (m.subtotal || (m.hours * m.ratePerHour) || 0), 0));

  // Command Deck dark palette — force the dark tokens regardless of app theme (inline only).
  const cdVars = {
    '--fj-card': '#12162A',
    '--fj-card2': 'rgba(255,255,255,.045)',
    '--fj-text': '#FFFFFF',
    '--fj-text2': 'rgba(255,255,255,.72)',
    '--fj-muted': 'rgba(255,255,255,.5)',
    '--fj-line': 'rgba(255,255,255,.12)',
    '--fj-line2': 'rgba(255,255,255,.09)',
    '--fj-chip': 'rgba(255,255,255,.07)',
    '--fj-inputbg': 'rgba(255,255,255,.05)',
    '--fj-inputbd': 'rgba(255,255,255,.14)',
    '--fj-earn-fg': '#67C6B4',
    '--fj-earn-bg': 'rgba(103,198,180,.10)',
    '--fj-wwcc-fg': '#8FB0FF',
    '--fj-wwcc-bg': 'rgba(59,130,246,.18)',
    '--fj-pop-fg': '#FBBF24',
    '--fj-pop-bg': 'rgba(251,191,36,.15)',
  };
  const cdBackdrop = { background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', padding: 20 };
  const cdCard = { background: '#12162A', border: '1px solid rgba(255,255,255,.12)', boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)' };
  const cdBtnAqua = { background: '#28C0D0', color: '#0C1120', border: 'none', boxShadow: '0 12px 28px -10px rgba(40,192,208,.55)' };
  const cdBtnGhost = { background: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.16)', color: '#fff' };

  return (
    <div className="fj-root fj-modal-backdrop" style={{ ...cdVars, zIndex: 150, ...cdBackdrop }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fj-modal-card fjscroll" style={{ ...cdCard, maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>

        {/* Header band — Command Deck: Violet → Magenta */}
        <div className="relative flex-shrink-0 px-6 py-5 text-white" style={{ background: 'linear-gradient(120deg, #572B7C, #78287C)' }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-[9px] bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
            style={{ border: '1px solid rgba(255,255,255,.18)' }}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 text-xs opacity-90 mb-2 flex-wrap pr-8">
            <span className="fj-mono bg-white/20 px-2 py-0.5 rounded-md">
              {job.job_number || `JOB-${job.id?.substring(0, 6).toUpperCase()}`}
            </span>
            <span className="bg-white/20 px-2.5 py-0.5 rounded-full font-semibold">{statusConfig.label}</span>
          </div>

          <h1 className="fj-h1 font-bold text-[22px] leading-tight tracking-[-0.4px]">
            {job.title || job.job_title}
          </h1>

          <div className="flex items-center gap-4 mt-2.5 text-sm opacity-90 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              <span>{jobDates.length} day{jobDates.length > 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              <span>{crewMembers.length} role{crewMembers.length > 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{job.location || job.location_address}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto fjscroll">
          <div className="p-6 space-y-5">

            {/* Stat grid: Dates · Roles · Applicants · Up to */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-muted)' }}>Dates</p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-text)' }}>{jobDates.length} day{jobDates.length > 1 ? 's' : ''}</p>
              </div>
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-muted)' }}>Roles</p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-text)' }}>{crewMembers.length} open</p>
              </div>
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-muted)' }}>Applicants</p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-text)' }}>{job.applicant_count ?? job.applicants ?? 0}</p>
              </div>
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-earn-fg)' }}>Up to</p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-earn-fg)' }}>{formatCurrency(totalOpenEarnings)}</p>
              </div>
            </div>

            {/* Job Schedule */}
            <div>
              <h3 className="fj-overline mb-2" style={{ color: 'var(--fj-muted)' }}>
                Job Schedule ({jobDates.length} day{jobDates.length > 1 ? 's' : ''})
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {jobDates.map((date, idx) => (
                  <span key={idx} className="fj-chip">{formatDate(date)}</span>
                ))}
              </div>
            </div>

            {/* Available Positions */}
            <div>
              <h3 className="fj-overline mb-3" style={{ color: 'var(--fj-muted)' }}>
                Available positions ({crewMembers.length})
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {crewMembers.map((member, idx) => {
                  const role = member.role;
                  const hasApplied = applicationsStatus[role];

                  return (
                    <div key={idx} className="fj-card rounded-[15px] p-4">
                      {/* Role Header */}
                      <div className="flex items-center gap-2.5 mb-3">
                        <span className="fj-dot w-2.5 h-2.5" style={{ background: roleColor(member.role) }} />
                        <div className="flex-1 min-w-0">
                          <h4 className="fj-name text-[15px] font-semibold capitalize truncate" style={{ color: 'var(--fj-text)' }}>
                            {member.role?.replace(/_/g, ' ') || 'Crew Member'}
                          </h4>
                        </div>
                        <span className="fj-num text-[18px] font-bold tracking-[-0.3px]" style={{ color: 'var(--fj-text)' }}>
                          {formatCurrency(getCrewPayout(member.subtotal || (member.hours * member.ratePerHour)))}
                        </span>
                      </div>

                      {/* Meta row: hours · rate · day count */}
                      <div className="flex items-center gap-3.5 text-xs mb-3" style={{ color: 'var(--fj-muted)' }}>
                        <span>{member.hours || 0} hrs</span>
                        <span>${getCrewPayout(member.ratePerHour || 0)}/hr</span>
                        {member.dateAssignments && member.dateAssignments.filter(a => a.hours > 0).length > 0 && (
                          <span>{member.dateAssignments.filter(a => a.hours > 0).length} day{member.dateAssignments.filter(a => a.hours > 0).length > 1 ? 's' : ''}</span>
                        )}
                      </div>

                      {/* Per-Date Breakdown */}
                      {member.dateAssignments && member.dateAssignments.filter(a => a.hours > 0).length > 0 && (
                        <div className="mb-3 pt-3 space-y-1.5" style={{ borderTop: '1px solid var(--fj-line2)' }}>
                          {member.dateAssignments.filter(a => a.hours > 0).map((assignment, assignIdx) => (
                            <div key={assignIdx} className="flex items-center justify-between px-3 py-2 rounded-[10px] text-sm" style={{ background: 'var(--fj-card2)' }}>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5" style={{ color: 'var(--fj-aqua)' }} />
                                <span className="font-semibold" style={{ color: 'var(--fj-text)' }}>{formatDate(assignment.date)}</span>
                              </div>
                              <span className="font-bold" style={{ color: 'var(--fj-earn-fg)' }}>{assignment.hours} hrs</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Apply Button */}
                      <div className="mt-3">
                        {hasApplied ? (
                          <div className="space-y-2.5">
                            <div className="flex items-center gap-2 p-3 rounded-[12px] fj-badge-wwcc">
                              <CheckCircle className="w-4 h-4" />
                              <span className="font-semibold text-sm">Applied for this position</span>
                            </div>
                            <button
                              onClick={() => handleRemoveApplication(role)}
                              disabled={loading[role]}
                              className="fj-btn fj-btn-outline w-full"
                              style={{ borderColor: 'rgba(244,63,94,.4)', color: '#F43F5E' }}
                            >
                              {loading[role] ? (
                                <>
                                  <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                                  Removing...
                                </>
                              ) : (
                                <>
                                  <X className="w-4 h-4" />
                                  Remove Application
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleOpenApplyModal(role, member)}
                            className="fj-btn fj-btn-primary w-full capitalize"
                            style={cdBtnAqua}
                          >
                            <Send className="w-4 h-4" />
                            Apply for {member.role?.replace(/_/g, ' ')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Job Brief */}
            <div>
              <h3 className="fj-overline mb-1.5" style={{ color: 'var(--fj-muted)' }}>Job brief</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--fj-text2)', whiteSpace: 'pre-wrap' }}>
                {linkifyText(job.description || job.brief || 'No description provided')}
              </p>
            </div>

            {/* WWCC strip */}
            {job.requiresWWCC && (
              <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 fj-badge-wwcc">
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span className="text-[13px] font-semibold">Working With Children Check required — you must have a valid WWCC to apply</span>
              </div>
            )}

            {/* Total Earnings for Open Positions */}
            <div className="rounded-[15px] p-5 text-center" style={{ background: 'var(--fj-earn-bg)' }}>
              <p className="fj-overline mb-1.5" style={{ color: 'var(--fj-earn-fg)' }}>
                Total Earnings ({crewMembers.length} Open Position{crewMembers.length !== 1 ? 's' : ''})
              </p>
              <p className="fj-num text-[34px] font-bold tracking-[-0.6px] mb-0.5" style={{ color: 'var(--fj-earn-fg)' }}>
                {formatCurrency(totalOpenEarnings)}
              </p>
              <p className="text-sm" style={{ color: 'var(--fj-earn-fg)', opacity: 0.85 }}>
                {jobDates.length} day{jobDates.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <p className="text-[8px] text-center py-1 opacity-20 select-none">modal:find-jobs-advanced-details</p>
        </div>
      </div>

      {/* Apply Modal */}
      {showApplyModal && selectedRoleToApply && (
        <div className="fj-root fj-modal-backdrop" style={{ ...cdVars, zIndex: 160, ...cdBackdrop }} onClick={(e) => { if (e.target === e.currentTarget) { setShowApplyModal(false); setSelectedRoleToApply(null); setApplicationMessage(''); } }}>
          <div className="fj-modal-card" style={{ ...cdCard, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="px-6 py-5 text-white flex-shrink-0" style={{ background: 'var(--fj-grad)' }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="fj-h3 text-lg font-bold">Apply to this job</h3>
                  <p className="text-sm text-white/85 truncate">{job.title || job.job_title}</p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5 overflow-y-auto fjscroll">
              {/* Role Summary Card */}
              <div className="rounded-[13px] p-4" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="fj-dot w-2.5 h-2.5" style={{ background: roleColor(selectedRoleToApply.role) }} />
                  <div className="min-w-0">
                    <p className="fj-name font-semibold text-[15px] capitalize" style={{ color: 'var(--fj-text)' }}>
                      {selectedRoleToApply.role?.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm truncate" style={{ color: 'var(--fj-muted)' }}>
                      {job.location || job.location_address}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-3" style={{ borderTop: '1px solid var(--fj-line2)' }}>
                  <div>
                    <p className="fj-overline text-[10px]" style={{ color: 'var(--fj-muted)' }}>Hours</p>
                    <p className="fj-num text-lg font-bold" style={{ color: 'var(--fj-text)' }}>
                      {selectedRoleToApply.crewMember?.hours || 0}h
                    </p>
                  </div>
                  <div>
                    <p className="fj-overline text-[10px]" style={{ color: 'var(--fj-muted)' }}>Rate</p>
                    <p className="fj-num text-lg font-bold" style={{ color: 'var(--fj-text)' }}>
                      ${getCrewPayout(selectedRoleToApply.crewMember?.ratePerHour || 0)}/hr
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between px-3 py-2.5 rounded-[10px]" style={{ background: 'var(--fj-earn-bg)' }}>
                  <span className="text-[13px] font-bold" style={{ color: 'var(--fj-earn-fg)' }}>You earn</span>
                  <span className="fj-num text-[20px] font-bold tracking-[-0.4px]" style={{ color: 'var(--fj-earn-fg)' }}>
                    {formatCurrency(getCrewPayout(selectedRoleToApply.crewMember?.subtotal || (selectedRoleToApply.crewMember?.hours * selectedRoleToApply.crewMember?.ratePerHour)))}
                  </span>
                </div>
              </div>

              {/* Introduction Message */}
              <div>
                <label className="fj-overline block mb-2" style={{ color: 'var(--fj-muted)' }}>
                  Introduction <span style={{ textTransform: 'none', fontWeight: 500 }}>(optional)</span>
                </label>
                <textarea
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  placeholder="Add a short note — relevant experience, availability, or any questions you have…"
                  rows={4}
                  className="fj-input"
                  style={{ resize: 'vertical', lineHeight: 1.5 }}
                />
                <p className="text-xs mt-2" style={{ color: 'var(--fj-muted)' }}>
                  A personalised message can help you stand out from other applicants
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 flex gap-2.5 flex-shrink-0" style={{ borderTop: '1px solid var(--fj-line)', background: 'var(--fj-card)' }}>
              <button
                onClick={() => {
                  setShowApplyModal(false);
                  setSelectedRoleToApply(null);
                  setApplicationMessage('');
                }}
                disabled={submittingApplication}
                className="fj-btn fj-btn-outline flex-shrink-0"
                style={cdBtnGhost}
              >
                Back
              </button>
              <button
                onClick={handleSubmitApplication}
                disabled={submittingApplication}
                className="fj-btn fj-btn-primary flex-1"
                style={cdBtnAqua}
              >
                {submittingApplication ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send application
                  </>
                )}
              </button>
            </div>
            <p className="text-[8px] text-center py-1 opacity-20 select-none">modal:find-jobs-advanced-apply</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedJobDetailsModalForCrew;
